<template>
  <div
    class="tc-overlay"
    role="presentation"
    @click.self="close"
  >
    <div
      ref="dialogEl"
      class="tc-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-checklist-title"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header class="tc-header">
        <div>
          <p class="tc-eyebrow">Getting Started</p>
          <h2 id="tutorial-checklist-title" class="tc-title">Explore BarbershopTalk</h2>
        </div>
        <button
          type="button"
          class="tc-close"
          aria-label="Close Getting Started"
          @click="close"
        >
          &times;
        </button>
      </header>

      <p class="tc-progress" aria-live="polite">
        {{ progress.done }} of {{ progress.total }} complete ({{ progress.percent }}%)
      </p>
      <div class="tc-progress-bar" aria-hidden="true">
        <span class="tc-progress-fill" :style="{ width: progress.percent + '%' }" />
      </div>

      <div class="tc-focus" aria-live="polite">
        <p class="tc-focus-label">Focus: {{ interestFocus.label }}</p>
        <p class="tc-focus-blurb">{{ interestFocus.blurb }}</p>
      </div>

      <ul class="tc-list">
        <li
          v-for="item in primaryItems"
          :key="item.id"
          class="tc-item"
          :class="{
            'tc-item--done': isComplete(item.id),
            'tc-item--active': isActive(item.id),
            'tc-item--suggested': isSuggested(item.id),
          }"
        >
          <div class="tc-item-main">
            <span class="tc-check" aria-hidden="true">
              <i v-if="isComplete(item.id)" class="fa fa-check" />
            </span>
            <div class="tc-item-copy">
              <p v-if="isSuggested(item.id)" class="tc-suggested">Suggested for you</p>
              <h3 class="tc-item-title">{{ item.title }}</h3>
              <p class="tc-item-body">{{ item.body }}</p>
              <div class="tc-item-actions">
                <button
                  type="button"
                  class="tc-btn tc-btn--primary"
                  @click="startItem(item)"
                >
                  {{ item.actionLabel }}
                </button>
                <button
                  v-if="item.featureTipId"
                  type="button"
                  class="tc-btn tc-btn--ghost"
                  @click="replayTip(item.featureTipId)"
                >
                  Replay tip
                </button>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <section class="tc-more" aria-labelledby="tutorial-more-title">
        <h3 id="tutorial-more-title" class="tc-more-title">More to try</h3>
        <p class="tc-more-blurb">Optional community stops. They do not count toward the primary checklist.</p>
        <ul class="tc-list tc-list--optional">
          <li
            v-for="item in optionalItems"
            :key="item.id"
            class="tc-item"
            :class="{ 'tc-item--done': isComplete(item.id) }"
          >
            <div class="tc-item-main">
              <span class="tc-check tc-check--sm" aria-hidden="true">
                <i v-if="isComplete(item.id)" class="fa fa-check" />
              </span>
              <div class="tc-item-copy">
                <h3 class="tc-item-title">{{ item.title }}</h3>
                <p class="tc-item-body">{{ item.body }}</p>
                <button
                  type="button"
                  class="tc-btn tc-btn--primary"
                  @click="startItem(item)"
                >
                  {{ item.actionLabel }}
                </button>
              </div>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useTutorialProgress } from '@/composables/useTutorialProgress';
import { OPTIONAL_ITEMS, getInterestFocus } from '@/constants/tutorialConfig';

export default {
  name: 'TutorialChecklistPanel',
  setup() {
    const tutorial = useTutorialProgress();
    const dialogEl = ref(null);
    const previousFocus = ref(null);

    const primaryItems = computed(() => tutorial.orderedPrimaryItems());
    const progress = computed(() => tutorial.primaryProgress());
    const optionalItems = OPTIONAL_ITEMS;
    const interestFocus = computed(() => getInterestFocus(tutorial.state.interest));

    function isComplete(id) {
      return tutorial.isItemComplete(id);
    }

    function isActive(id) {
      return tutorial.state.activeTutorialItemId === id;
    }

    function isSuggested(id) {
      if (isComplete(id)) return false;
      const suggested = interestFocus.value?.suggestedIds || [];
      return suggested.includes(id);
    }

    function close() {
      tutorial.closeChecklist();
    }

    function startItem(item) {
      tutorial.openItem(item);
    }

    function replayTip(id) {
      tutorial.closeChecklist();
      tutorial.replayCoachmark(id);
    }

    function focusTrap(e) {
      if (e.key !== 'Tab' || !dialogEl.value) return;
      const focusable = dialogEl.value.querySelectorAll(
        'button:not([disabled]), [href], summary, [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusable);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      focusTrap(e);
    }

    onMounted(() => {
      previousFocus.value = document.activeElement;
      nextTick(() => {
        if (dialogEl.value) dialogEl.value.focus();
      });
    });

    onBeforeUnmount(() => {
      const el = previousFocus.value;
      if (el && typeof el.focus === 'function') {
        try { el.focus(); } catch { /* ignore */ }
      }
    });

    return {
      dialogEl,
      primaryItems,
      optionalItems,
      progress,
      interestFocus,
      isComplete,
      isActive,
      isSuggested,
      close,
      startItem,
      replayTip,
      onKeydown,
    };
  },
};
</script>

<style scoped>
.tc-overlay {
  position: fixed;
  inset: 0;
  z-index: 3350;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px;
  padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(6px);
  box-sizing: border-box;
  text-align: left;
}

.tc-dialog {
  width: 100%;
  max-width: 520px;
  max-height: min(90vh, 720px);
  overflow: auto;
  background: #ffffff;
  color: #0f172a;
  border-radius: 18px 18px 12px 12px;
  padding: 20px 18px 24px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  font-family: 'Titillium Web', sans-serif;
  box-sizing: border-box;
}

.tc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.tc-eyebrow {
  margin: 0 0 4px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #0b7ee3;
}

.tc-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: -0.02em;
}

.tc-close {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  font-size: 1.6rem;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px;
}

.tc-close:focus-visible {
  outline: 3px solid rgba(11, 126, 227, 0.35);
  outline-offset: 2px;
}

.tc-progress {
  margin: 0 0 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #64748b;
}

.tc-progress-bar {
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  margin-bottom: 18px;
  overflow: hidden;
}

.tc-progress-fill {
  display: block;
  height: 100%;
  background: #0b7ee3;
  border-radius: 999px;
}

.tc-focus {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(11, 126, 227, 0.08);
  border: 1px solid rgba(11, 126, 227, 0.18);
}

.tc-focus-label {
  margin: 0 0 4px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0b7ee3;
}

.tc-focus-blurb {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: #334155;
}

.tc-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tc-item {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  background: #f8fafc;
}

.tc-item--active {
  border-color: rgba(11, 126, 227, 0.55);
  box-shadow: inset 0 0 0 1px rgba(11, 126, 227, 0.2);
}

.tc-item--suggested:not(.tc-item--done) {
  border-color: rgba(11, 126, 227, 0.35);
  background: #f0f7ff;
}

.tc-suggested {
  margin: 0 0 4px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0b7ee3;
}

.tc-item--done {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.tc-item-main {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.tc-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  color: #16a34a;
  font-size: 0.75rem;
}

.tc-item--done .tc-check {
  border-color: #16a34a;
  background: #dcfce7;
}

.tc-check--sm {
  width: 20px;
  height: 20px;
}

.tc-item-title {
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 800;
}

.tc-item-body {
  margin: 0 0 12px;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #475569;
}

.tc-item-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tc-btn {
  min-height: 44px;
  padding: 8px 14px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
}

.tc-btn--primary {
  background: #0b7ee3;
  color: #fff;
  border: none;
}

.tc-btn--ghost {
  background: #fff;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.tc-btn:focus-visible {
  outline: 3px solid rgba(11, 126, 227, 0.35);
  outline-offset: 2px;
}

.tc-more {
  margin-top: 20px;
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
}

.tc-more-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 900;
  color: #0f172a;
}

.tc-more-blurb {
  margin: 0 0 12px;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #64748b;
}

.tc-list--optional {
  margin-top: 0;
}

@media (min-width: 700px) {
  .tc-overlay {
    align-items: center;
    padding: 24px;
  }
  .tc-dialog {
    border-radius: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tc-progress-fill {
    transition: none;
  }
}
</style>
